/* eslint-disable no-param-reassign */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * MathTextComponent - Wrapper component for rendering text with MathJax and Markdown support
 *
 * @file MathTextComponent.jsx
 * @author Murhsid Ahamedh
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import ContentHelper from '../../common/contentHelper';
import { loadMathMlDependencies } from '../../common/MathMl';

/**
 * Normalize escaped backslashes from API responses
 * Handles cases where API returns double-escaped backslashes
 * Examples: \\( becomes \(, \\frac becomes \frac
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
const normalizeEscapedBackslashes = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Replace sequences of backslashes:
  // - \\\\( becomes \\( (two backslashes -> one)
  // - \\\\frac becomes \\frac (two backslashes -> one)
  // This handles cases where JSON has double-escaped backslashes
  // Note: JSON.parse() usually handles this, but some APIs might return already-escaped strings
  return text.replace(/\\\\+/g, (match) => {
    // If even number of backslashes, reduce by half
    // If odd, keep one and reduce the rest
    const count = match.length;
    if (count % 2 === 0) {
      return '\\'.repeat(count / 2);
    }

    return `\\${'\\'.repeat((count - 1) / 2)}`;
  });
};

/**
 * Check if content contains math expressions
 * @param {string} text - Text to check
 * @returns {boolean} - True if math content is detected
 */
const hasMathContent = (text) => {
  if (!text || typeof text !== 'string') return false;

  // Check for various math delimiters (including escaped versions)
  return (
    (text.indexOf('`') >= 0 &&
      text.lastIndexOf('`') > 0 &&
      text.indexOf('`') !== text.lastIndexOf('`')) ||
    text.indexOf('\\') >= 0 ||
    text.includes('</math>') ||
    text.includes('\\(') ||
    text.includes('\\)') ||
    text.includes('\\\\(') ||
    text.includes('\\\\)') ||
    text.includes('\\[') ||
    text.includes('\\]') ||
    text.includes('\\\\[') ||
    text.includes('\\\\]') ||
    text.includes('$$')
  );
};

const MathTextComponent = ({
  text,
  className,
  component,
  variant,
  ...typographyProps
}) => {
  const ref = useRef(null);
  const [processedContent, setProcessedContent] = useState(text);
  const [isProcessing, setIsProcessing] = useState(false);
  const containsMath = hasMathContent(text);

  // Process content through markdown and MathJax
  useEffect(() => {
    if (!text || typeof text !== 'string') {
      setProcessedContent(text || '');

      return;
    }

    const processContent = async () => {
      setIsProcessing(true);
      try {
        // Normalize escaped backslashes from API responses first
        const normalizedText = normalizeEscapedBackslashes(text);

        // Load MathJax and Marked dependencies if needed
        if (containsMath || normalizedText.trim().length > 0) {
          await loadMathMlDependencies();
        }

        // Process through markdown first
        let processed = await ContentHelper.convertToMarkedDownString(normalizedText);

        // Then process through MathJax if math content exists
        if (containsMath) {
          processed = await ContentHelper.mathjaxConversion(processed);
        }

        setProcessedContent(processed);
      } catch (error) {
        console.error('Error processing content:', error);
        // Fallback to original text on error
        setProcessedContent(text);
      } finally {
        setIsProcessing(false);
      }
    };

    processContent();
  }, [text, containsMath]);

  // Handle MathJax typesetting after content is set
  useEffect(() => {
    if (!containsMath || !ref.current || isProcessing) {
      return;
    }

    let isMounted = true;

    const typesetContent = async () => {
      try {
        await loadMathMlDependencies();

        if (!isMounted || !ref.current) {
          return;
        }

        const mathjax = window.MathJax;
        if (!mathjax) {
          return;
        }

        // Use typesetPromise if available (MathJax 3.x)
        if (typeof mathjax.typesetPromise === 'function') {
          mathjax.typesetPromise([ref.current])
            .then(() => {
              // Reset margins after MathJax typesetting
              if (ref.current) {
                const nestedElements = ref.current.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li');
                nestedElements.forEach((el) => {
                  el.style.marginBlockStart = '0';
                  el.style.marginBlockEnd = '0';
                });
              }
            })
            .catch(() => {
              // Silently handle errors
            });

          return;
        }

        // Fallback to typeset (MathJax 2.x)
        if (typeof mathjax.typeset === 'function') {
          mathjax.typeset([ref.current]);
          // Reset margins after MathJax typesetting
          setTimeout(() => {
            if (ref.current) {
              const nestedElements = ref.current.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li');
              nestedElements.forEach((el) => {
                el.style.marginBlockStart = '0';
                el.style.marginBlockEnd = '0';
              });
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error typesetting content:', error);
        // Silently handle errors
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      typesetContent();
    }, 0);

    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [processedContent, containsMath, isProcessing]);

  // Set innerHTML directly using useEffect
  useEffect(() => {
    if (ref.current) {
      if (processedContent !== null && processedContent !== undefined && processedContent !== '') {
        // Set innerHTML directly instead of using dangerouslySetInnerHTML
        ref.current.innerHTML = processedContent;

        // Reset margins on nested p tags and other block elements
        const nestedElements = ref.current.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li');
        nestedElements.forEach((el) => {
          el.style.marginBlockStart = '0';
          el.style.marginBlockEnd = '0';
        });
      } else {
        // Clear content if empty
        ref.current.innerHTML = '';
      }
    }
  }, [processedContent]);

  // Extract style from typographyProps to merge properly
  const { style: propsStyle, ...restProps } = typographyProps;

  return (
    <Typography
      ref={ref}
      className={className}
      component={component}
      variant={variant}
      style={{
        marginBlockStart: 0,
        marginBlockEnd: 0,
        ...propsStyle
      }}
      {...restProps}
    />
  );
};

MathTextComponent.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
  variant: PropTypes.string
};

MathTextComponent.defaultProps = {
  className: '',
  component: 'span',
  variant: 'body1'
};

export default MathTextComponent;

